import { Add, Cached, Chat, Check, Clear, DeleteOutline, Edit, Email, PictureAsPdf, ReportProblem, WhatsApp } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Breadcrumbs, Button, Checkbox, Chip, FormControl, IconButton, InputLabel, Link, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';
import { useAtom } from 'jotai';
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { documentsAtom, modalConfirmSend, modalDeleteDelivery, modalNewDeliveryCompany, modalNewDeliveryDocument, permissionsAtom, selectedDocumentsAtom } from '../../atoms/atom';
import AppBar from "../../components/app-bar/AppBar";
import { useDialog } from '../../components/contextProviders/DialogContext';
import ConfirmSendModal from '../../components/documents/confirm-send-modal/ConfirmSendModal';
import CreateCompanyModal from '../../components/documents/create-company-modal/CreateCompanyModal';
import CreateDeliveryModal from '../../components/documents/create-delivery-modal/CreateDeliveryModal';
import CreateTemplateModal from '../../components/documents/create-template-modal/CreateTemplateModal';
import DeleteDeliveryModal from '../../components/documents/delete-delivery-modal/DeleteDeliveryModal';
import { DocumentsService } from '../../services/DocumentService';
import { getDeliveryDateFilters } from '../../types/ComparationFilters';
import { DeliveryDocumentDTO } from '../../types/DeliveryDocumentDTO';
import { DeliveryDocumentsByCompanyDTO } from '../../types/DeliveryDocumentsByCompanyDTO';
import { TemplateDTO } from '../../types/TemplateDTO';
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from '../execution-events/ExecutionEvents';
import './DeliveryDocuments.scss';

const DeliveryDocuments: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [documents, setDocuments] = useAtom(documentsAtom);
	const [expandedCompany, setExpandedCompany] = useState<string | false>('');
	const [filter, setFilter] = useState('TODOS');
	const [dateFilter, setDateFilter] = useState('ALL');
	const [initialDate, setInitialDate] = useState<Dayjs | null>(null);
	const [finalDate, setFinalDate] = useState<Dayjs | null>(null);
	const [loading, setLoading] = useState(true);
	const [selectedCompany, setSelectedCompany] = useState<DeliveryDocumentsByCompanyDTO | null>(null);
	const [openNewCompany, setOpenNewCompany] = useAtom(modalNewDeliveryCompany);
	const [openNewDelivery, setOpenNewDelivery] = useAtom(modalNewDeliveryDocument);
	const [selectedDocuments, setSelectedDocuments] = useAtom(selectedDocumentsAtom);
	const [openConfirmSend, setOpenConfirmSend] = useAtom(modalConfirmSend);
	const [selectedToReprocess, setSelected] = useState({} as DeliveryDocumentDTO);
	const [selectedToDelete, setSelectedToDelete] = useState({} as DeliveryDocumentDTO);
	const [openDeleteDelivery, setOpenDeleteDelivery] = useAtom(modalDeleteDelivery);
	const [templates, setTemplates] = useState<TemplateDTO[]>([]);
	const [permissions] = useAtom(permissionsAtom);
	const service = new DocumentsService();
	const { verifyPermission, formatStringFullDate } = useUtils();
	const navigate = useNavigate();
	const { showDialog } = useDialog();

	const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpandedCompany(isExpanded ? panel : false);
	};

	const back = () => {
		navigate(-1);
	}

	useEffect(() => {
		if (openNewCompany || openNewDelivery || openConfirmSend || openDeleteDelivery) return;
		const loadComparations = async () => {
			const result = await service.getDeliveriesByGroup(id || '');
			if (result) {
				setDocuments(result.data);
			}
			setLoading(false);
		};
		const loadTemplates = async () => {
			const response = await service.getTemplates();
			if (response && response.data) {
				setTemplates(response.data);
			}
		};
		loadComparations();
		loadTemplates();
		setSelectedDocuments([]);
	}, [id, openNewCompany, openNewDelivery, openConfirmSend, openDeleteDelivery]);

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'DOCUMENTS');
		}
	}, []);

	const openDocument = (uuid: string) => {
		window.open(`${window.location.origin}/api/documents/view/internal/${uuid}`, "_blank")
	}

	return(
		<div className="comparations">
			<AppBar
				modulo="Envio de documentos"
			/>
			<div className="component">
				<div className="top">
					<div className="etapas">
						<Breadcrumbs aria-label="breadcrumb" sx={{display: "flex", alignItems: "flex-end"}}>
							<Link variant="h6" sx={{cursor: "pointer"}} underline="hover" color="inherit" onClick={() => back()}>
								Envio de documentos
							</Link>
							<Link
								variant="subtitle1"
								underline="hover"
								color="text.primary"
								aria-current="page"
								sx={{display: "flex", alignItems: "flex-end !important"}}
							>
								{documents?.companyNameGroup}
							</Link>
						</Breadcrumbs>
					</div>
					<div className="search">
						<Button
							variant='contained'
							color='success'
							startIcon={<Add />}
							onClick={() => setOpenNewCompany(true)}
						>
							Adicionar empresa
						</Button>
						<FormControl size='small' sx={{width: "200px"}}>
							<InputLabel>
								Filtrar tipo
							</InputLabel>
							<Select
								label='Filtrar tipo'
								id="select-tipo"
								value={filter}
								onChange={(e) => setFilter(e.target.value)}
							>
								{templates.map(item => (
									<MenuItem
										key={item.title}
										value={item.title}
									>
										{item.title}
									</MenuItem>
								))}
								<MenuItem
									key={'TODOS'}
									value={'TODOS'}
								>
									Todos
								</MenuItem>
							</Select>
						</FormControl>
						<FormControl size='small' sx={{width: "200px"}}>
							<InputLabel>
								Filtrar por data
							</InputLabel>
							<Select
								label='Filtrar por data'
								id="select-date"
								value={dateFilter}
								onChange={(e) => setDateFilter(e.target.value)}
							>
								{getDeliveryDateFilters().map(item => (
									<MenuItem
										key={item.id}
										value={item.id}
									>
										{item.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{display: dateFilter === 'CUSTOMIZED' ? "flex" : "none"}}>
							<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
								<DemoContainer components={['DatePicker']}>
									<DatePicker
										className='initial-date'
										label="Data inicial"
										value={initialDate}
										sx={{ minWidth: '150px !important', width: "160px" }}
										onChange={(newValue) => setInitialDate(newValue)}
										disableFuture
										slotProps={{ textField: { size: 'small'}}}
									/>
									<DatePicker
										className='final-date'
										label="Data final"
										value={finalDate}
										sx={{ minWidth: '150px !important', width: "160px", marginLeft: "9px !important" }}
										onChange={(newValue) => setFinalDate(newValue)}
										disableFuture
										slotProps={{ textField: { size: 'small'}}}
									/>
								</DemoContainer>
							</LocalizationProvider>
						</Box>
						{filter !== 'TODOS' || dateFilter !== 'ALL'
							? <Button
								variant='contained'
								color='error'
								onClick={() => {
									setFilter('TODOS');
									setDateFilter('ALL');
									setInitialDate(null);
									setFinalDate(null);
								}}
							>
								Limpar filtros
							</Button>
							: <></>
						}
					</div>
				</div>
				<div className="tabela">
					<div className="listagem">
						{!documents?.companies.length && !loading
							? <div className="sem-execucoes">Nenhum registro encontrado para o filtro aplicado...</div>
							: loading
								? getLoading()
								: <></>
						}
						{documents?.companies.map(company => (
							<Accordion
								expanded={expandedCompany === company.companyName || documents?.companies.length === 1}
								onChange={handleChange(company.companyName)}
								slotProps={{ transition: { unmountOnExit: true } }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls={company.companyName}
									id={company.companyName}
								>
									<Typography fontSize="2.1vh" sx={{width: "32%"}}>{company.companyName}</Typography>
									<Button
										variant='contained'
										sx={{maxHeight: "4vh"}}
										onClick={() => {
											setSelectedCompany(company);
											setSelected({} as DeliveryDocumentDTO);
											setOpenNewDelivery(true);
										}}
									>
										Enviar novo documento
									</Button>
									{selectedDocuments.length
										? <Button
											variant='contained'
											sx={{maxHeight: "4vh", marginLeft: "10px"}}
											color='secondary'
											onClick={() => setOpenConfirmSend(true)}
										>
											Enviar selecionados
										</Button>
										: <></>
									}
									<Typography fontSize="2.1vh" sx={{width: "32%", textAlign: "end"}}>
										{getLastDelivery(company.documents)}
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }}>
										<Table stickyHeader sx={{ minWidth: 650, display: company.documents.length ? "" : "none !important" }} aria-label="simple table">
											<TableHead>
											<TableRow>
												<TableCell sx={{fontSize: "1.6vh"}}>Tipo de documento</TableCell>
												<TableCell className='th-styled-cell'>Enviado</TableCell>
												<TableCell className='th-styled-cell'>Canal</TableCell>
												<TableCell className='th-styled-cell'>Contato</TableCell>
												<TableCell className='th-styled-cell'>Data de envio</TableCell>
												<TableCell className='th-styled-cell'>Documento enviado</TableCell>
												<TableCell className='th-styled-cell'>Mensagem enviada</TableCell>
												<TableCell className='th-styled-cell'>Arquivo visualizado pelo cliente</TableCell>
												<TableCell className='th-styled-cell'>Ações</TableCell>
											</TableRow>
											</TableHead>
											<TableBody>
											{filterDocuments(company.documents).map((document) => (
												<TableRow
													key={document.id}
													sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
													className='row-line'
												>
													<TableCell component="th">{document.deliveryType}</TableCell>
													<TableCell className="th-styled-cell">
														{document.error
															? <Tooltip title={document.error}>
																	<ReportProblem color='error'/>
																</Tooltip>
															: document.sent
																? <Check color='success'/>
																: <Clear color='error'/>
														}
													</TableCell>
													<TableCell className="th-styled-cell">{iconChannel(document.channel)}</TableCell>
													<TableCell align="center" className='th-styled-cell'>
														<div style={{ 
															display: 'flex', 
															flexWrap: 'wrap',
															justifyContent: 'center',
															alignItems: 'center',
															gap: '8px'
														}}>
															<Stack direction="row" spacing={1} sx={{flexWrap: "wrap", justifyContent: "center", width: "300px", gap: "3px"}}>
																{document.destinatary?.split(", ").map((cnpj) => (
																	<Chip
																		key={cnpj}
																		label={cnpj}
																	/>
																))}
															</Stack>
														</div>
													</TableCell>
													<TableCell className='th-styled-cell'>{document.sent ? formatStringFullDate(document.createdAt) : ""}</TableCell>
													<TableCell className='th-styled-cell'>
														{document.documentUuid && document.documentUuid.split(",").map((uuid) => (
															<Tooltip key={uuid} title={`Visualizar documento ${uuid}`}>
																<IconButton onClick={() => openDocument(uuid)}>
																	<PictureAsPdf color='warning'/>
																</IconButton>
															</Tooltip>
														))}
													</TableCell>
													<TableCell className='th-styled-cell'>
														<Tooltip title="Visualizar mensagem enviada">
															<IconButton onClick={() => showDialog("Mensagem enviada", document.message)}>
																<Chat color='success'/>
															</IconButton>
														</Tooltip>
													</TableCell>
													<TableCell className='th-styled-cell'>{iconRead(document.readByClient, document.readAt)}</TableCell>
													<TableCell className='th-styled-cell'>
														{document.sent
															? <Tooltip title="Reenviar">
																<IconButton onClick={() => {
																	setSelected(document);
																	setOpenNewDelivery(true);
																	setSelectedCompany(company);
																}}>
																	<Cached color='secondary'/>
																</IconButton>
															</Tooltip>
															: <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
																<Tooltip title="Selecionar para enviar">
																	<Checkbox checked={selectedDocuments.includes(document.id)} onChange={() => {
																		if (selectedDocuments.includes(document.id)) {
																			setSelectedDocuments(selectedDocuments.filter(id => id !== document.id));
																		} else {
																			setSelectedDocuments([...selectedDocuments, document.id]);
																		}
																	}}/>
																</Tooltip>
																<Tooltip title="Editar envio pendente">
																	<IconButton onClick={() => {
																		setSelected(document);
																		setOpenNewDelivery(true);
																		setSelectedCompany(company);
																	}}>
																		<Edit color='info'/>
																	</IconButton>
																</Tooltip>
																<Tooltip title="Excluir envio pendente">
																	<IconButton onClick={() => {
																		setSelectedToDelete(document);
																		setOpenDeleteDelivery(true);
																	}}>
																		<DeleteOutline color='error'/>
																	</IconButton>
																</Tooltip>
															</Box>
														}
													</TableCell>
												</TableRow>
											))}
											</TableBody>
										</Table>
									</TableContainer>
								</AccordionDetails>
							</Accordion>
						))}
					</div>
				</div>
			</div>
			<CreateCompanyModal modulo={id || ''}/>
			<CreateDeliveryModal
				company={selectedCompany || {} as DeliveryDocumentsByCompanyDTO}
				dto={selectedToReprocess}
				edit={!!selectedToReprocess.id}
			/>
			<CreateTemplateModal/>
			<ConfirmSendModal/>
			<DeleteDeliveryModal dto={selectedToDelete}/>
		</div>
	);

	function iconRead(value: boolean, readAt: string) {
		return <Tooltip title={value ? `Lido em ${formatStringFullDate(readAt)}` : "Não lido"}>
			{value ? <Check color='success'/> : <Clear color='error'/>}
		</Tooltip>;
	}

	function iconChannel(value: string) {
		if (value === 'WHATSAPP') {
			return <WhatsApp color='success'/>;
		}
		if (value === 'EMAIL') {
			return <Email color='info'/>;
		}
		return <>
			<WhatsApp color='success' sx={{marginRight: "0.7vh"}}/>
			<Email color='info'/>
		</>
	}

	function filterDocuments(list: DeliveryDocumentDTO[]) {
		let filtered;
		if (filter === 'TODOS') {
			filtered = list;
		} else {
			filtered = list.filter(item => item.deliveryType === filter);
		}
		if (dateFilter === "ALL") {
			return filtered;
		}
		let start: Dayjs | null = null;
		let end: Dayjs | null = dayjs().endOf('day');
		switch (dateFilter) {
			case 'THIS_MONTH':
				start = dayjs().startOf('month');
				end = dayjs().endOf('month');
				break;
			case 'LAST_30':
				start = dayjs().subtract(30, 'day').startOf('day');
				break;
			case 'LAST_90':
				start = dayjs().subtract(90, 'day').startOf('day');
				break;
			case 'THIS_YEAR':
				start = dayjs().startOf('year');
				end = dayjs().endOf('year');
				break;
			case 'CUSTOMIZED':
				start = initialDate ? initialDate.startOf('day') : null;
				end = finalDate ? finalDate.endOf('day') : null;
				break;
		}
		return filtered.filter(item => {
			if (!item.createdAt) return false;
			const itemDate = dayjs(item.createdAt);
			console.log(itemDate);
			if (start && itemDate.isBefore(start)) {
				return false;
			}
			if (end && itemDate.isAfter(end)) {
				return false;
			}
			return true;
		});
	}

	function getLastDelivery(list: DeliveryDocumentDTO[]) {
		if (!list || !list.length) return "Nenhum documento enviado";
		const last = list.reduce((prev, current) => {
			return dayjs(prev.createdAt).isAfter(dayjs(current.createdAt)) ? prev : current;
		});
		return "Último documento enviado: " + formatStringFullDate(last.createdAt);
	}
}

export default DeliveryDocuments;